import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeo } from "@/components/feature/Seo";
import FigureCard from "@/components/feature/FigureCard";
import {
  figures,
  figureCategories,
  type FigureCategory,
} from "@/mocks/figures";

const origin = "https://focuspeople.example";

export default function FigureLibrary() {
  const [active, setActive] = useState<string>("all");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    let result =
      active === "all" ? figures : figures.filter((f) => f.category === active);
    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter((f) =>
        `${f.name}${f.role}${f.summary}${f.keywords.join("")}`
          .toLowerCase()
          .includes(q),
      );
    }
    return result;
  }, [active, query]);

  useSeo({
    title: "人物档案库｜最新爆料中的新主角与当事人",
    description:
      "最新爆料人物档案库，以角色视角归档每一个浮出的新面孔、新涉事者与新当事人，支持按举报与揭发、涉事主角、关联当事人、回应与进展分类检索。",
    keywords: "最新爆料,人物档案,新主角,新面孔,新涉事者,新当事人",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "人物档案库",
        url: `${origin}/renwu`,
        description:
          "最新爆料人物追踪的人物档案库，归档新面孔、新涉事者与新当事人及其角色分类。",
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "全部人物档案",
        itemListElement: figures.map((f, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: f.name,
          url: `${origin}/renwu/${f.id}`,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: "人物档案库",
            item: `${origin}/renwu`,
          },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 pt-8 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-secondary-700">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-950">人物档案库</span>
          </nav>

          <div className="mt-6 border-b border-background-200 pb-7">
            <p className="text-xs font-semibold tracking-widest text-secondary-700">
              FULL ARCHIVE
            </p>
            <h1 className="mt-2 font-display text-3xl font-black text-foreground-950 md:text-4xl">
              人物档案库
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              以角色视角归档每一桩最新爆料中浮现的新主角、新面孔、新涉事者与新当事人。可使用下方分类与搜索快速定位，点击卡片进入完整追踪档案。
            </p>
          </div>

          <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {figureCategories.map((c: FigureCategory) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setActive(c.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active === c.key
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-secondary-200"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-secondary-700"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索人物 / 角色 / 关键词"
                aria-label="搜索人物档案"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6">
          <p className="mb-5 text-sm text-secondary-700">
            共 {list.length} 份人物档案{active !== "all" || query ? "（已筛选）" : ""}
          </p>
          {list.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <i className="ri-file-search-line text-4xl text-secondary-500"></i>
              <p className="mt-4 text-sm text-foreground-600">
                未找到匹配的人物档案，可尝试更换关键词或分类。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((f) => (
                <FigureCard key={f.id} figure={f} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}